import {
  BellOutlined,
  ClockCircleOutlined,
  EditFilled,
  TagOutlined,
} from "@ant-design/icons";
import { AppPaths } from "@p0security/shared/routes/constants";
import { ApprovalRule } from "@p0security/shared/types/workflow/types";
import { Flex, Typography } from "antd";
import {
  RoutingRulePopover,
  StyledRoutingRulePreview,
} from "components/Jit/Routing/styles";
import { getApprovalIcon } from "components/Jit/Routing/views/common";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import { useFirestoreCollection } from "providers/FirestoreProvider";
import { useCallback, useMemo } from "react";

import { ApprovalIdpGroupPreview, IdpGroupsPreview } from "./IdpGroupPreview";

export type ApprovalRulePreview = ApprovalRule | { type: "genericGroup" };

type Props = {
  approval: ApprovalRulePreview;
};

export const ApprovalPreview: React.FC<Props> = ({ approval }) => {
  const { docs: p0Approvers } = useFirestoreCollection<{ email: string }>(
    "roles/manager/bindings",
    {
      tenantAware: true,
    }
  );

  const p0ApproversList = useMemo(() => {
    return (
      <>
        <Flex vertical>
          {p0Approvers?.map((doc, index) => (
            <Typography.Paragraph key={index}>
              {doc.data.email}
            </Typography.Paragraph>
          ))}
        </Flex>
        <TenantAwareLink to={AppPaths.P0Admin}>
          Edit this group <EditFilled />
        </TenantAwareLink>
      </>
    );
  }, [p0Approvers]);

  const genericList = useCallback((ids?: string[]) => {
    return (
      <>
        <Flex vertical gap={8}>
          {ids?.map((id, index) => (
            <Typography.Paragraph key={index}>{id}</Typography.Paragraph>
          ))}
        </Flex>
      </>
    );
  }, []);

  return approval.type === "genericGroup" ? (
    <ApprovalIdpGroupPreview />
  ) : approval.type === "group" ? (
    <IdpGroupsPreview
      idpGroups={approval.groups}
      isApproval
      effect={approval.effect}
    />
  ) : approval.type === "auto" ? (
    <StyledRoutingRulePreview>
      <ClockCircleOutlined />
      <span>Allowed if on-call</span>
    </StyledRoutingRulePreview>
  ) : approval.type === "ticket" ? (
    <StyledRoutingRulePreview>
      <TagOutlined />
      <span>Allowed with matching ticket</span>
    </StyledRoutingRulePreview>
  ) : approval.type === "p0" ? (
    <RoutingRulePopover
      text="Approval by P0 Security Reviewers"
      icon={getApprovalIcon("p0")}
      popoverContent={p0ApproversList}
    />
  ) : approval.type === "persistent" ? (
    <StyledRoutingRulePreview>
      {getApprovalIcon("persistent")}
      <span>Always allowed</span>
    </StyledRoutingRulePreview>
  ) : approval.type === "escalation" ? (
    <RoutingRulePopover
      text={`Approval by on-call`}
      icon={<BellOutlined />}
      popoverContent={
        approval.services && approval.services.length > 0 ? (
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {genericList(approval.services)}
          </Typography.Paragraph>
        ) : undefined
      }
    />
  ) : approval.type === "requestor-profile" ? (
    <RoutingRulePopover
      text="Approval by manager"
      icon={getApprovalIcon("requestor-profile")}
      popoverContent={
        <>
          <Typography.Paragraph italic>
            Requires an installed Okta, Entra ID or Google Workspace
            integration.
          </Typography.Paragraph>
          <Typography.Paragraph>
            Requests may be approved by the requestor&apos;s manager, as defined
            by the presence of the manager&apos;s email in the user&apos;s
            Identity Provider profile.
          </Typography.Paragraph>
        </>
      }
    />
  ) : (
    <StyledRoutingRulePreview>
      {getApprovalIcon("deny")}
      <span>Deny all access</span>
    </StyledRoutingRulePreview>
  );
};
